import { useEffect, useMemo } from 'react';
import { Box, InputAdornment, Stack, StackProps, Typography } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { Control, SetFieldValue, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormDatePicker } from '../../main-app/components/form/FormDatePicker';
import { FormInput } from '../../main-app/components/form/FormInput';
import { FormNumericInput, FormNumericInputProps } from '../../main-app/components/form/FormNumericInput';
import { FormSelect } from '../../main-app/components/form/FormSelect';
import { FormSwitch } from '../../main-app/components/form/FormSwitch';
import { ConnectedFormFieldGroup } from '../../main-app/modules/forms/invoice/connected/ConnectedFormFieldGroup';
import { currencies } from '../../main-app/utils/currencies';
import { ConnectedInvoiceItemsFormPart } from './ConnectedInvoiceItemsFormPart';
import { inputSharedProps } from '../../main-app/modules/forms/invoice/shared';

type Props = StackProps<'form'> & {
  readonly?: boolean;
};

export const ConnectedInvoice = ({ readonly, ...stackProps }: Props) => {
  const { t } = useTranslation();

  const { control, watch, setValue } = useFormContext();

  if (!control) {
    throw new Error('Form context is not available. Use ConnectedInvoice only in FormProvider.');
  }

  const selectedCurrency = watch('currency');
  const taxPayer = watch('taxPayer');

  // Clear taxPayer related fields if taxPayer is false
  useEffect(() => {
    if (!readonly && taxPayer === false) {
      setValue('amountWithoutVat', null, { shouldValidate: true });
      setValue('amountVat', null, { shouldValidate: true });
      setValue('taxPoint', null, { shouldValidate: true });
      setValue('vendor.taxId', null, { shouldValidate: true });
    }
  }, [taxPayer, setValue, readonly]);

  const currencyProps = useMemo(() => {
    const currency = currencies.find((curr) => curr.value.toLowerCase() === selectedCurrency?.toLowerCase());
    return {
      position: currency?.position ?? 'suffix',
      unicode: currency?.unicode ?? '',
    };
  }, [selectedCurrency]);

  return (
    <Stack {...stackProps} component={'form'} autoComplete={'off'}>
      <Grid
        container
        spacing={{
          xs: 2,
          md: 6,
        }}
      >
        <Grid
          xs={12}
          container
          spacing={{
            xs: 2,
            md: 6,
          }}
        >
          <Grid xs={6}>
            <Typography variant={'h4'}>{t('invoice')}</Typography>
          </Grid>
          <Grid xs={6}>
            <FormInput
              readonly={readonly}
              control={control}
              name={'number'}
              label={t('invoice.number') + '*'}
              {...inputSharedProps}
            />
          </Grid>
        </Grid>
        <Grid xs={12} md={6} pb={0} container spacing={2}>
          {/* Vendor */}
          <Grid xs={8}>
            <Typography variant={'h4'}>{t('vendor')}</Typography>
          </Grid>
          <Grid xs={4}>{!readonly && <FormSwitch control={control} name={'taxPayer'} label={t('taxPayer')} />}</Grid>
          <Grid xs={12} md={6}>
            <FormInput
              readonly={readonly}
              control={control}
              name={'vendor.vatId'}
              label={t('vatId') + '*'}
              {...inputSharedProps}
            />
          </Grid>
          <Grid xs={12} md={6}>
            <FormInput
              readonly={readonly}
              control={control}
              name={'vendor.taxId'}
              label={t('taxId') + (taxPayer ? ' *' : '')}
              disabled={!taxPayer}
              {...inputSharedProps}
            />
          </Grid>
          <Grid xs={12}>
            <FormInput
              readonly={readonly}
              control={control}
              name={'vendor.name'}
              label={t('businessName') + '*'}
              {...inputSharedProps}
            />
          </Grid>
          <Grid xs={12}>
            <FormInput
              readonly={readonly}
              control={control}
              multiline={true}
              minRows={3}
              name={'vendor.address'}
              label={t('streetAddress') + '*'}
              {...inputSharedProps}
            />
          </Grid>
          <Grid xs={12} md={6}>
            <FormInput
              readonly={readonly}
              control={control}
              name={'vendor.email'}
              label={t('email')}
              {...inputSharedProps}
            />
          </Grid>
          <Grid xs={12} md={6}>
            <FormInput
              readonly={readonly}
              control={control}
              name={'vendor.phoneNumber'}
              label={t('phone')}
              {...inputSharedProps}
            />
          </Grid>
          <Grid xs={12}>
            <FormInput
              readonly={readonly}
              control={control}
              name={'vendor.registry'}
              label={'Označení registru, ve kterém jste zapsaní' + '*'}
              {...inputSharedProps}
            />
          </Grid>
        </Grid>
        <Grid xs={12} md={6} pb={0} container spacing={2} alignContent={'flex-start'}>
          {/* CUSTOMER */}
          <Grid xs={12}>
            <Typography variant={'h4'}>{t('customer')}</Typography>
          </Grid>
          <Grid xs={12} md={6}>
            <FormInput
              readonly={readonly}
              control={control}
              name={'customer.vatId'}
              label={t('vatId')}
              {...inputSharedProps}
            />
          </Grid>
          <Grid xs={12} md={6}>
            <FormInput
              readonly={readonly}
              control={control}
              name={'customer.taxId'}
              label={t('taxId')}
              {...inputSharedProps}
            />
          </Grid>
          <Grid xs={12}>
            <FormInput
              readonly={readonly}
              control={control}
              name={'customer.name'}
              label={t('businessName') + '*'}
              {...inputSharedProps}
            />
          </Grid>
          <Grid xs={12}>
            <FormInput
              readonly={readonly}
              control={control}
              multiline={true}
              minRows={3}
              name={'customer.address'}
              label={t('streetAddress')}
              {...inputSharedProps}
            />
          </Grid>
          <Grid xs={12} md={6}>
            <FormInput
              readonly={readonly}
              control={control}
              name={'customer.email'}
              label={t('email')}
              {...inputSharedProps}
            />
          </Grid>
          <Grid xs={12} md={6}>
            <FormInput
              readonly={readonly}
              control={control}
              name={'customer.phoneNumber'}
              label={t('phone')}
              {...inputSharedProps}
            />
          </Grid>
        </Grid>
        <Grid xs={12} container spacing={2}>
          <Grid xs={12} paddingBottom={0}>
            <Typography variant={'h4'}>{t('paymentInfo')}</Typography>
          </Grid>
          <Grid
            xs={12}
            container
            spacing={{
              xs: 2,
              md: 8,
            }}
          >
            <Grid xs={12} md={6} container>
              <Grid xs={12}>
                <ConnectedFormFieldGroup
                  readonly={readonly}
                  label={'\xa0'}
                  formNames={[]}
                  sx={{
                    border: 0,
                    px: 0,
                  }}
                >
                  <Grid xs={12} container spacing={2}>
                    <Grid xs={12} md={6}>
                      <FormDatePicker
                        readonly={readonly}
                        control={control}
                        name={'issueDate'}
                        label={t('issueDate') + '*'}
                        textFieldProps={{
                          ...inputSharedProps,
                        }}
                      />
                    </Grid>
                    <Grid xs={12} md={6}>
                      <FormDatePicker
                        readonly={readonly}
                        control={control}
                        name={'dueDate'}
                        label={t('dueDate') + '*'}
                        textFieldProps={{
                          ...inputSharedProps,
                        }}
                      />
                    </Grid>
                    {readonly && !taxPayer ? null : (
                      <Grid xs={12} md={6}>
                        <FormDatePicker
                          readonly={readonly}
                          control={control}
                          name={'taxPoint'}
                          label={t('taxPoint') + (taxPayer ? ' *' : '')}
                          disabled={!taxPayer}
                          textFieldProps={{
                            ...inputSharedProps,
                          }}
                        />
                      </Grid>
                    )}
                    <Grid xs={12} md={6}>
                      <FormInput
                        control={control}
                        name={'variableSymbol'}
                        label={t('variableSymbol') + '*'}
                        readonly={readonly}
                        {...inputSharedProps}
                      />
                    </Grid>
                  </Grid>
                </ConnectedFormFieldGroup>
              </Grid>
            </Grid>
            <Grid xs={12} md={6} container>
              <Grid xs={12}>
                <ConnectedFormFieldGroup
                  readonly={readonly}
                  required={true}
                  label={'Bankovní informace'}
                  helperText={t('IbanOrBankAccountAreRequired') as string}
                  formNames={['bankAccount', 'bankNumber', 'iban', 'swift']}
                >
                  <Grid xs={12} container spacing={2}>
                    <Grid xs={8}>
                      <FormInput
                        disableRequiredErrorIndication={true}
                        readonly={readonly}
                        control={control}
                        name={'bankAccount'}
                        label={t('bankAccount')}
                        InputProps={{
                          endAdornment: <InputAdornment position={'end'}>/</InputAdornment>,
                        }}
                        {...inputSharedProps}
                      />
                    </Grid>
                    <Grid xs={4}>
                      <FormInput
                        disableRequiredErrorIndication={true}
                        readonly={readonly}
                        control={control}
                        name={'bankNumber'}
                        label={t('bankNumber')}
                        {...inputSharedProps}
                      />
                    </Grid>
                    <Grid xs={12} md={8}>
                      <FormInput
                        disableRequiredErrorIndication={true}
                        readonly={readonly}
                        control={control}
                        name={'iban'}
                        label={t('iban')}
                        {...inputSharedProps}
                      />
                    </Grid>
                    <Grid xs={12} md={4}>
                      <FormInput
                        disableRequiredErrorIndication={true}
                        readonly={readonly}
                        control={control}
                        name={'swift'}
                        label={t('swift')}
                        {...inputSharedProps}
                      />
                    </Grid>
                  </Grid>
                </ConnectedFormFieldGroup>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid xs={12} container>
          <Grid xs={12} paddingBottom={0}>
            <Typography variant={'h4'}>{'Položky faktury'}</Typography>
          </Grid>
          <Grid xs={12}>
            <ConnectedInvoiceItemsFormPart
              readonly={readonly}
              inputProps={{
                ...inputSharedProps,
              }}
            />
          </Grid>
        </Grid>
        <Box
          sx={{
            height: '1px',
            borderTopWidth: 1,
            borderTopStyle: 'dashed',
            borderTopColor: 'divider',
            position: 'relative',
            width: '100%',
            mt: { xs: 4, md: 1 },
            mb: { xs: 2, md: 0 },
          }}
        ></Box>
        <Grid xs={12} container spacing={2}>
          {readonly && !taxPayer ? null : (
            <Grid xs={8}>
              <AutoFilledAmountWithoutVatInput
                setValue={setValue}
                baseName={'items'}
                readonly={true}
                disabled={!taxPayer}
                control={control}
                name={'amountWithoutVat'}
                label={t('invoice.amountWithoutVat') + (taxPayer ? ' *' : '')}
                helperText={''}
                numericInputProps={{
                  [currencyProps.position]: ` ${currencyProps?.unicode} `,
                }}
                {...inputSharedProps}
              />
            </Grid>
          )}
          {readonly && !taxPayer ? null : <Grid xs={4}>{/* Empty grid for alignment */}</Grid>}
          {readonly && !taxPayer ? null : (
            <Grid xs={8}>
              <AutoFilledVatSumInput
                setValue={setValue}
                baseName={'items'}
                readonly={true}
                disabled={!taxPayer}
                control={control}
                name={'amountVat'}
                label={t('invoice.amountVat') + (taxPayer ? ' *' : '')}
                helperText={''}
                numericInputProps={{
                  [currencyProps.position]: ` ${currencyProps?.unicode} `,
                }}
                {...inputSharedProps}
              />
            </Grid>
          )}
          {readonly && !taxPayer ? null : <Grid xs={4}>{/* Empty grid for alignment */}</Grid>}
          <Grid xs={8}>
            <AutoFilledPriceSumInput
              readonly={readonly}
              control={control}
              name={'amount'}
              baseName={'items'}
              setValue={setValue}
              label={t('invoice.amount') + '*'}
              numericInputProps={{
                [currencyProps.position]: ` ${currencyProps?.unicode} `,
              }}
              {...inputSharedProps}
            />
          </Grid>
          <Grid xs={4}>
            <FormSelect
              readonly={readonly}
              control={control}
              name={'currency'}
              label={t('currency') + '*'}
              options={currencies}
              {...inputSharedProps}
            />
          </Grid>
        </Grid>
      </Grid>
    </Stack>
  );
};

const AutoFilledPriceSumInput = ({
  control,
  setValue,
  baseName,
  ...rest
}: {
  control: Control;
  setValue: SetFieldValue<any>;
  baseName: string;
} & FormNumericInputProps) => {
  const results = useWatch({
    control: control,
    name: baseName,
    defaultValue: '',
  });

  const sum = useMemo(
    () =>
      Array.isArray(results)
        ? results?.reduce((acc, item) => {
            return (acc += parseFloat(item.priceSum || 0));
          }, 0)
        : undefined,
    [results],
  );

  useEffect(() => {
    if (sum !== undefined) {
      setValue(rest.name, sum);
    }
  }, [sum, setValue, rest.name]);

  return <FormNumericInput control={control} {...rest} />;
};

const AutoFilledVatSumInput = ({
  control,
  setValue,
  baseName,
  ...rest
}: {
  control: Control;
  setValue: SetFieldValue<any>;
  baseName: string;
} & FormNumericInputProps) => {
  const results = useWatch({
    control: control,
    name: baseName,
    defaultValue: '',
  });

  const taxPayer = useWatch({
    control,
    name: 'taxPayer',
  });

  // Calculate the total VAT amount
  const totalVat = useMemo(() => {
    if (!Array.isArray(results) || !taxPayer) {
      return 0;
    }

    return results.reduce((total, item) => {
      const priceWithoutVat = parseFloat(item.priceWithoutVat || 0);
      const priceWithVat = parseFloat(item.priceSum || 0);
      if (!isNaN(priceWithoutVat) && !isNaN(priceWithVat)) {
        // The VAT amount is the difference between price with VAT and price without VAT
        return total + (priceWithVat - priceWithoutVat);
      }
      return total;
    }, 0);
  }, [results, taxPayer]);

  useEffect(() => {
    if (totalVat !== undefined) {
      setValue(rest.name, totalVat.toFixed(2));
    }
  }, [totalVat, setValue, rest.name]);

  return <FormNumericInput control={control} {...rest} />;
};

const AutoFilledAmountWithoutVatInput = ({
  control,
  setValue,
  baseName,
  ...rest
}: {
  control: Control;
  setValue: SetFieldValue<any>;
  baseName: string;
} & FormNumericInputProps) => {
  const results = useWatch({
    control: control,
    name: baseName,
    defaultValue: '',
  });

  const taxPayer = useWatch({
    control,
    name: 'taxPayer',
  });

  // Calculate the total amount without VAT
  const totalWithoutVat = useMemo(() => {
    if (!taxPayer) {
      return;
    }

    if (!Array.isArray(results)) {
      return 0;
    }

    return results.reduce((total, item) => {
      const priceWithoutVat = parseFloat(item.priceWithoutVat || 0);
      if (!isNaN(priceWithoutVat)) {
        return total + priceWithoutVat;
      }

      return total;
    }, 0);
  }, [results, taxPayer]);

  useEffect(() => {
    if (totalWithoutVat) {
      setValue(rest.name, totalWithoutVat.toFixed(2));
    }
  }, [totalWithoutVat, setValue, rest.name]);

  return <FormNumericInput control={control} {...rest} />;
};
